import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, firstValueFrom, Subject, take } from 'rxjs';
import {
  ElectronDisplayMediaPickerDialogComponent,
  ElectronDisplayMediaPickerDialogResult,
} from 'src/app/components/main/shared/electron-display-media-picker-dialog/electron-display-media-picker-dialog.component';
import { ApiService } from './api.service';
import {
  DisplayMediaSourceOption,
  ElectronDisplayMediaPickerRequest,
  MicrophoneAccessRequestResult,
  PlatformService,
  ScreenAccessRequestResult,
} from './platform.service';
import {
  CreateMediaRecordingBody,
  MediaRecording,
  MediaRecordingCaptureMode,
  MediaRecordingMediaType,
} from '../models/media-recording.model';
import { MediaRecordingUploadRetryKindEnum } from 'src/app/enums/media-recording-upload-retry-kind.enum';
import { MediaRecordingUploadFailurePhaseEnum } from 'src/app/enums/media-recording-upload-failure-phase.enum';
import {
  areMediaRecordingChunksCompleteOnServer,
  isMediaRecordingLinkedToMeetingRecord,
  MediaRecordingUploadRetryContext,
  resolveMediaRecordingUploadRetryContext,
} from '../components/main/shared/meeting-record/meeting-record.component';
import { MediaRecordingServerProcessingLabelPipe } from 'src/app/pipes/media-recording-server-processing-label.pipe';
import { MeetingRecordMediaRecordingPusherUpdate } from '../models/meeting-record-media-recording-pusher-update.model';
import { PusherService } from './pusher.service';

export interface ActiveRecordingState {
  client_local_id: string;
  server_id: number | null;
  concept: string;
  youtube_title: string | null;
  media_type: MediaRecordingMediaType;
  capture_mode: MediaRecordingCaptureMode;
  include_microphone: boolean;
  include_screen: boolean;
  include_system_audio: boolean;
  status:
    | 'recording'
    | 'paused'
    | 'stopping'
    | 'stopped'
    | 'uploading'
    | 'pending_upload'
    | 'upload_failed'
    | 'processing'
    | 'completed'
    | 'failed';
  elapsed_seconds: number;
  upload_progress_percent: number;
  upload_progress_mode: 'determinate' | 'indeterminate';
  upload_status_label: string | null;
  upload_retry_button_label: string | null;
  upload_retry_summary: string | null;
  error_message: string | null;
  recordable_type: string | null;
  recordable_id: number | null;
}

export interface RecordingSessionEndedEvent {
  recordable_type: string | null;
  recordable_id: number | null;
  server_id: number | null;
}

interface OfflineChunkRecord {
  id?: string;
  client_local_id: string;
  chunk_index: number;
  segment_index: number;
  part_index: number;
  blob: Blob;
  duration_ms?: number;
}

interface OfflineVideoRecord {
  client_local_id: string;
  blob: Blob;
}

export interface RecordingPendingUploadMetadata {
  client_local_id: string;
  concept: string;
  youtube_title?: string | null;
  media_type: MediaRecordingMediaType;
  capture_mode: MediaRecordingCaptureMode;
  recordable_type?: string | null;
  recordable_id?: number | null;
  server_id?: number | null;
  pending_chunk_indexes: number[];
  expected_chunk_count?: number;
  status: 'pending_upload' | 'uploading' | 'upload_failed';
  duration_seconds?: number;
  updated_at?: string;
}

type OfflineRecordingMetadata = RecordingPendingUploadMetadata;

export interface RecoverableRecordingSession {
  client_local_id: string;
  server_id: number | null;
  concept: string;
  youtube_title: string | null;
  media_type: MediaRecordingMediaType;
  capture_mode: MediaRecordingCaptureMode;
  include_microphone: boolean;
  include_screen: boolean;
  include_system_audio: boolean;
  status: 'recording' | 'paused';
  elapsed_seconds: number;
  recordable_type: string | null;
  recordable_id: number | null;
  chunk_index: number;
  recording_segment_index: number;
  recording_part_index: number;
  uploaded_chunk_count: number;
  interrupted_at: string;
}

export interface RecordingMicrophoneOption {
  device_id: string;
  label: string;
}

export interface RecordingMicrophoneLoadResult {
  devices: RecordingMicrophoneOption[];
  error_message: string | null;
}

export interface RecordingDisplaySourcesLoadResult {
  sources: DisplayMediaSourceOption[];
  supports_system_audio: boolean;
  error_message: string | null;
}

/** Debe caber en upload_max_filesize de PHP (p. ej. 2M por defecto). */
const SERVER_UPLOAD_SLICE_BYTES = 1536 * 1024;
/**
 * Duración de cada segmento MediaRecorder independiente (start/stop).
 * Cada segmento es un WebM válido con cabecera EBML; se sube y libera RAM.
 */
const RECORDING_SEGMENT_DURATION_MS = 60_000;
const LIVE_PENDING_CHUNK_RETRY_DELAY_MS = 5_000;
const DB_NAME = 'bisual-recordings';
const DB_VERSION = 1;
const CHUNKS_STORE = 'chunks';
const VIDEO_STORE = 'video_blobs';
const METADATA_KEY = 'bisual_recording_queue_metadata';
const ACTIVE_SESSION_KEY = 'bisual_active_recording_session';
const MEDIA_RECORDING_TERMINAL_POLL_INTERVAL_MS = 60_000;
const MEDIA_RECORDING_TERMINAL_MAX_WAIT_MS = 7_200_000;

@Injectable({ providedIn: 'root' })
export class RecordingService {
  private api = inject(ApiService);
  private platform = inject(PlatformService);
  private mat_dialog = inject(MatDialog);
  private pusher = inject(PusherService);
  private media_recording_server_processing_label_pipe = inject(MediaRecordingServerProcessingLabelPipe);

  private media_recorder: MediaRecorder | null = null;
  private media_stream: MediaStream | null = null;
  private chunk_index = 0;
  private recording_segment_index = 0;
  private recording_part_index = 0;
  private uploaded_chunk_count = 0;
  private pending_chunk_uploads = new Set<Promise<void>>();
  private live_chunk_upload_chain: Promise<void> = Promise.resolve();
  private live_pending_chunk_retry_timer: ReturnType<typeof setTimeout> | null = null;
  private auto_dismiss_timer: ReturnType<typeof setTimeout> | null = null;
  private elapsed_timer: ReturnType<typeof setInterval> | null = null;
  private chunk_blobs: Blob[] = [];
  private display_audio_track_ids = new Set<string>();
  private captured_system_audio = false;
  private is_discarding = false;
  private is_stopping = false;
  private audio_context: AudioContext | null = null;
  private track_ended_abort_controller: AbortController | null = null;
  private db_promise: Promise<IDBDatabase> | null = null;
  private segment_rotation_timer: ReturnType<typeof setTimeout> | null = null;
  private recording_mime_type: string | null = null;
  private recording_media_type: MediaRecordingMediaType | null = null;
  private recording_client_local_id: string | null = null;
  private pending_electron_display_selection: {
    source_id: string;
    include_system_audio: boolean;
  } | null = null;

  private active_state_subject = new BehaviorSubject<ActiveRecordingState | null>(null);
  active_state$ = this.active_state_subject.asObservable();

  private pending_uploads_subject = new BehaviorSubject<OfflineRecordingMetadata[]>([]);
  pending_uploads$ = this.pending_uploads_subject.asObservable();

  private recoverable_session_subject = new BehaviorSubject<RecoverableRecordingSession | null>(null);
  recoverable_session$ = this.recoverable_session_subject.asObservable();

  private recording_session_ended_subject = new Subject<RecordingSessionEndedEvent>();
  recordingSessionEnded$ = this.recording_session_ended_subject.asObservable();

  private readonly before_unload_handler = (event: BeforeUnloadEvent): void => {
    if (!this.isRecordingActive()) {
      return;
    }

    this.syncPersistedActiveRecordingSession();
    event.preventDefault();
  };

  private readonly page_hide_handler = (): void => {
    this.syncPersistedActiveRecordingSession();
  };

  constructor() {
    this.refreshPendingUploads();
    this.loadRecoverableRecordingSession();
    window.addEventListener('online', () => {
      void this.drainOfflineQueue();
    });
    window.addEventListener('beforeunload', this.before_unload_handler);
    window.addEventListener('pagehide', this.page_hide_handler);
    void this.drainOfflineQueue();
  }

  getActiveState(): ActiveRecordingState | null {
    return this.active_state_subject.value;
  }

  refreshPendingUploads(): void {
    this.pruneEmptyPendingUploadMetadata();

    const active_state = this.active_state_subject.value;
    const visible_pending_uploads = this.getMetadataQueue().filter(pending =>
      this.shouldShowPendingUploadBanner(pending, active_state, undefined)
    );

    this.pending_uploads_subject.next(visible_pending_uploads);
  }

  dismissActiveRecording(): void {
    this.clearAutoDismissTimer();
    this.active_state_subject.next(null);
  }

  /** Limpia el chip/estado UI al cerrar sesión; no borra grabaciones en servidor ni cola offline. */
  clearSessionOnLogout(): void {
    this.clearAutoDismissTimer();
    this.active_state_subject.next(null);
    this.recoverable_session_subject.next(null);
  }

  async resumeInterruptedRecording(): Promise<void> {
    this.assertRecordingAllowedOnPlatform();

    const recoverable_session = this.recoverable_session_subject.value ?? this.readPersistedActiveRecordingSession();

    if (recoverable_session === null) {
      return;
    }

    const blocked_message = this.getNewRecordingBlockedMessage();

    if (blocked_message !== null) {
      throw new Error(blocked_message);
    }

    this.clearAutoDismissTimer();
    this.dismissActiveRecording();
    this.clearRecoverableRecordingSession();
    this.is_stopping = false;

    this.chunk_index = recoverable_session.chunk_index;
    this.recording_segment_index = recoverable_session.recording_segment_index;
    this.recording_part_index = recoverable_session.recording_part_index;
    this.uploaded_chunk_count = recoverable_session.uploaded_chunk_count;
    this.chunk_blobs = [];
    this.recording_client_local_id = recoverable_session.client_local_id;

    const recording_config = this.resolveRecordingConfig(recoverable_session.include_microphone, recoverable_session.include_screen);

    this.media_stream = await this.acquireMediaStream(recoverable_session.include_microphone, recoverable_session.include_screen, null);
    this.attachMediaStreamTrackEndedListeners();

    const include_system_audio = this.hasSystemAudioTrack(this.media_stream);
    const mime_type = this.resolveMimeType(recording_config.media_type);

    this.recording_mime_type = mime_type;
    this.recording_media_type = recording_config.media_type;

    let server_id = recoverable_session.server_id;

    if (server_id !== null && navigator.onLine) {
      const media_recording = await firstValueFrom(this.api.getMediaRecording(server_id));

      if (media_recording.recording_status !== 'recording' && media_recording.recording_status !== 'paused') {
        throw new Error('La grabación en el servidor ya no se puede reanudar.');
      }

      if (media_recording.recording_status === 'paused') {
        const resumed = await firstValueFrom(this.api.resumeMediaRecording(server_id));
        server_id = resumed.id;
      }
    }

    this.setActiveState({
      client_local_id: recoverable_session.client_local_id,
      server_id,
      concept: recoverable_session.concept,
      youtube_title: recoverable_session.youtube_title,
      media_type: recording_config.media_type,
      capture_mode: recording_config.capture_mode,
      include_microphone: recoverable_session.include_microphone,
      include_screen: recoverable_session.include_screen,
      include_system_audio,
      status: 'recording',
      elapsed_seconds: recoverable_session.elapsed_seconds,
      upload_progress_percent: 0,
      upload_progress_mode: 'determinate',
      upload_status_label: null,
      upload_retry_button_label: null,
      upload_retry_summary: null,
      error_message: null,
      recordable_type: recoverable_session.recordable_type,
      recordable_id: recoverable_session.recordable_id,
    });

    this.startElapsedTimer();
    this.startCurrentRecordingSegment();
  }

  async abandonRecoverableRecording(): Promise<void> {
    const recoverable_session = this.recoverable_session_subject.value ?? this.readPersistedActiveRecordingSession();

    if (recoverable_session === null) {
      return;
    }

    const should_discard = confirm('¿Descartar la grabación interrumpida? Se eliminará lo guardado localmente y no podrás reanudarla.');

    if (!should_discard) {
      return;
    }

    await this.purgeStoredRecordingUpload(recoverable_session.client_local_id, recoverable_session.server_id);
    this.clearRecoverableRecordingSession();
  }

  async discardFailedOrPendingUpload(client_local_id: string): Promise<boolean> {
    if (this.isRecordingActive() && this.active_state_subject.value?.client_local_id === client_local_id) {
      await this.discardRecording();

      return this.active_state_subject.value?.client_local_id !== client_local_id;
    }

    const metadata = this.getMetadataQueue().find(item => item.client_local_id === client_local_id);
    const active_state = this.active_state_subject.value;
    const matches_active = active_state?.client_local_id === client_local_id;
    const is_failed_or_pending_active =
      matches_active &&
      (active_state.status === 'upload_failed' || active_state.status === 'pending_upload' || active_state.status === 'failed');

    if (metadata === undefined && !is_failed_or_pending_active) {
      return false;
    }

    const should_discard = confirm(
      '¿Descartar esta subida? Se borrarán los fragmentos de este navegador y podrás iniciar una nueva grabación. No se podrá recuperar.'
    );

    if (!should_discard) {
      return false;
    }

    const server_id = metadata?.server_id ?? (matches_active ? (active_state.server_id ?? null) : null);
    const recordable_type = metadata?.recordable_type ?? (matches_active ? active_state.recordable_type : null) ?? null;
    const recordable_id = metadata?.recordable_id ?? (matches_active ? active_state.recordable_id : null) ?? null;

    await this.purgeStoredRecordingUpload(client_local_id, server_id ?? null);

    if (matches_active) {
      this.dismissActiveRecording();
    }

    this.notifyRecordingSessionEnded({
      recordable_type,
      recordable_id,
      server_id: null,
    });

    return true;
  }

  async clearLocalRecordingUploadData(client_local_id: string): Promise<void> {
    await this.purgeLocalRecordingArtifacts(client_local_id);

    if (this.active_state_subject.value?.client_local_id === client_local_id) {
      this.dismissActiveRecording();
    }

    this.refreshPendingUploads();
  }

  async startRecording(params: {
    concept: string;
    youtube_title?: string | null;
    include_microphone: boolean;
    include_screen: boolean;
    microphone_device_id?: string | null;
    recordable_type?: string | null;
    recordable_id?: number | null;
    electron_display_selection?: {
      source_id: string;
      include_system_audio: boolean;
    } | null;
  }): Promise<void> {
    this.assertRecordingAllowedOnPlatform();

    const blocked_message = this.getNewRecordingBlockedMessage();

    if (blocked_message !== null) {
      throw new Error(blocked_message);
    }

    if (!params.include_microphone && !params.include_screen) {
      throw new Error('Activa al menos el micrófono o la pantalla para grabar.');
    }

    this.clearAutoDismissTimer();
    this.dismissActiveRecording();
    this.is_stopping = false;

    const recording_config = this.resolveRecordingConfig(params.include_microphone, params.include_screen);

    const client_local_id = crypto.randomUUID();
    this.chunk_index = 0;
    this.recording_segment_index = 0;
    this.recording_part_index = 0;
    this.uploaded_chunk_count = 0;
    this.chunk_blobs = [];
    this.pending_electron_display_selection = params.electron_display_selection ?? null;

    this.media_stream = await this.acquireMediaStream(
      params.include_microphone,
      params.include_screen,
      params.microphone_device_id ?? null
    );
    this.attachMediaStreamTrackEndedListeners();
    const include_system_audio = this.hasSystemAudioTrack(this.media_stream);
    const youtube_title = params.youtube_title?.trim() || null;
    const mime_type = this.resolveMimeType(recording_config.media_type);

    this.recording_mime_type = mime_type;
    this.recording_media_type = recording_config.media_type;
    this.recording_client_local_id = client_local_id;

    const body: CreateMediaRecordingBody = {
      concept: params.concept,
      client_local_id,
      media_type: recording_config.media_type,
      capture_mode: recording_config.capture_mode,
      recordable_type: params.recordable_type ?? null,
      recordable_id: params.recordable_id ?? null,
    };

    let server_id: number | null = null;

    if (navigator.onLine) {
      try {
        const created = await firstValueFrom(this.api.createMediaRecording(body));
        server_id = created.id;
        this.removeMetadata(client_local_id);
        this.refreshPendingUploads();
      } catch (error) {
        if (error instanceof HttpErrorResponse && (error.status === 400 || error.status === 422)) {
          this.releaseMediaStream();
          this.recording_client_local_id = null;
          throw new Error(this.extractErrorMessage(error));
        }

        // La sesión en servidor se creará al subir el primer chunk (ensureServerSession).
      }
    }

    this.setActiveState({
      client_local_id,
      server_id,
      concept: params.concept,
      youtube_title,
      media_type: recording_config.media_type,
      capture_mode: recording_config.capture_mode,
      include_microphone: params.include_microphone,
      include_screen: params.include_screen,
      include_system_audio,
      status: 'recording',
      elapsed_seconds: 0,
      upload_progress_percent: 0,
      upload_progress_mode: 'determinate',
      upload_status_label: null,
      upload_retry_button_label: null,
      upload_retry_summary: null,
      error_message: null,
      recordable_type: params.recordable_type ?? null,
      recordable_id: params.recordable_id ?? null,
    });

    this.startElapsedTimer();
    this.startCurrentRecordingSegment();
  }

  async pauseRecording(): Promise<void> {
    const state = this.active_state_subject.value;

    if (!state || state.status === 'stopping' || this.is_stopping || !this.media_recorder || this.media_recorder.state !== 'recording') {
      return;
    }

    this.clearSegmentRotationTimer();
    this.media_recorder.pause();
    this.stopElapsedTimer();

    if (state.server_id && navigator.onLine) {
      const updated = await firstValueFrom(this.api.pauseMediaRecording(state.server_id));
      this.patchActiveState({ status: 'paused', server_id: updated.id });
    } else {
      this.patchActiveState({ status: 'paused' });
    }
  }

  async resumeRecording(): Promise<void> {
    this.assertRecordingAllowedOnPlatform();

    const state = this.active_state_subject.value;

    if (!state || state.status === 'stopping' || this.is_stopping || !this.media_recorder || this.media_recorder.state !== 'paused') {
      return;
    }

    this.media_recorder.resume();
    this.startElapsedTimer();
    this.patchActiveState({ status: 'recording' });
    this.scheduleSegmentRotation();

    if (state.server_id && navigator.onLine) {
      const updated = await firstValueFrom(this.api.resumeMediaRecording(state.server_id));
      this.patchActiveState({ server_id: updated.id });
    }
  }

  async discardRecording(): Promise<void> {
    const state = this.active_state_subject.value;

    if (!state || !this.isRecordingActive()) {
      return;
    }

    const should_discard = confirm('¿Descartar la grabación? Se detendrá y se eliminará todo sin posibilidad de recuperación.');

    if (!should_discard) {
      return;
    }

    this.is_discarding = true;
    this.clearAutoDismissTimer();
    this.clearSegmentRotationTimer();
    this.clearLivePendingChunkRetryTimer();

    const client_local_id = state.client_local_id;
    const server_id = state.server_id;

    if (this.media_recorder && this.media_recorder.state !== 'inactive') {
      const recorder = this.media_recorder;
      this.media_recorder = null;
      recorder.stop();
    } else {
      this.media_recorder = null;
    }

    this.stopElapsedTimer();
    this.releaseMediaStream();
    this.pending_chunk_uploads.clear();
    this.chunk_index = 0;
    this.uploaded_chunk_count = 0;
    this.chunk_blobs = [];
    this.recording_segment_index = 0;
    this.recording_part_index = 0;
    this.recording_mime_type = null;
    this.recording_media_type = null;
    this.recording_client_local_id = null;

    await this.purgeStoredRecordingUpload(client_local_id, server_id);

    this.is_discarding = false;
    this.clearRecoverableRecordingSession();
    this.dismissActiveRecording();
    this.notifyRecordingSessionEnded({
      recordable_type: state.recordable_type,
      recordable_id: state.recordable_id,
      server_id: null,
    });
  }

  async stopRecording(): Promise<void> {
    const state = this.active_state_subject.value;

    if (!state || !this.media_recorder || state.status === 'stopping' || this.is_stopping) {
      return;
    }

    const stopped_client_local_id = state.client_local_id;

    this.is_stopping = true;
    this.patchActiveState({ status: 'stopping' });
    this.clearLivePendingChunkRetryTimer();

    try {
      await this.finalizeCurrentSegment();
      const expected_chunk_count = this.chunk_index;

      this.stopElapsedTimer();
      this.releaseMediaStream();

      const duration_seconds = state.elapsed_seconds;
      let server_id = state.server_id;

      if (this.recording_client_local_id === stopped_client_local_id) {
        this.media_recorder = null;
        this.recording_mime_type = null;
        this.recording_media_type = null;
        this.recording_client_local_id = null;
      }
      this.is_stopping = false;
      this.clearRecoverableRecordingSession();

      this.patchActiveState({
        status: 'uploading',
        upload_progress_percent: 0,
        upload_progress_mode: 'determinate',
        upload_status_label: 'Subiendo fragmentos…',
        error_message: null,
      });

      if (navigator.onLine) {
        server_id = await this.ensureServerSession(state, server_id);

        if (server_id) {
          try {
            await firstValueFrom(
              this.api.stopMediaRecording(server_id, {
                duration_seconds,
                expected_chunk_count,
              })
            );

            await this.uploadRemainingChunks(stopped_client_local_id, server_id);

            await this.finalizeMediaUpload(server_id, {
              ...state,
              client_local_id: stopped_client_local_id,
              server_id,
              status: 'uploading',
              elapsed_seconds: duration_seconds,
            });
            this.removeMetadata(stopped_client_local_id);
            this.refreshPendingUploads();
            this.notifyRecordingSessionEnded({
              recordable_type: state.recordable_type,
              recordable_id: state.recordable_id,
              server_id,
            });
          } catch (error) {
            this.markUploadFailed(stopped_client_local_id, error);
            this.refreshPendingUploads();
            this.notifyRecordingSessionEnded({
              recordable_type: state.recordable_type,
              recordable_id: state.recordable_id,
              server_id: server_id ?? state.server_id,
            });
          }
        } else {
          this.notifyRecordingSessionEnded({
            recordable_type: state.recordable_type,
            recordable_id: state.recordable_id,
            server_id: null,
          });
        }
      } else {
        this.persistOfflineMetadata({
          client_local_id: stopped_client_local_id,
          concept: state.concept,
          youtube_title: state.youtube_title,
          media_type: state.media_type,
          capture_mode: state.capture_mode,
          recordable_type: state.recordable_type,
          recordable_id: state.recordable_id,
          server_id,
          pending_chunk_indexes: Array.from({ length: expected_chunk_count }, (_, i) => i),
          status: 'pending_upload',
          duration_seconds,
        });
        this.refreshPendingUploads();
        this.notifyRecordingSessionEnded({
          recordable_type: state.recordable_type,
          recordable_id: state.recordable_id,
          server_id: server_id ?? null,
        });
      }
    } finally {
      this.is_stopping = false;
    }
  }

  hasLocalRetryDataInQueue(client_local_id: string): boolean {
    return this.getMetadataQueue().some(item => item.client_local_id === client_local_id);
  }

  async hasLocalRetryData(client_local_id: string): Promise<boolean> {
    if (this.hasLocalRetryDataInQueue(client_local_id)) {
      return true;
    }

    const pending_chunks = await this.getChunksForRecording(client_local_id);

    return pending_chunks.length > 0;
  }

  async retryServerProcessing(server_id: number, client_local_id: string | null = null): Promise<void> {
    const active_state = this.active_state_subject.value;
    const should_update_active_state =
      active_state !== null && (client_local_id === null || active_state.client_local_id === client_local_id);

    const recordable_context = await this.resolveRecordableContextForServerRetry(server_id, active_state);

    if (should_update_active_state) {
      this.patchActiveState({
        server_id,
        status: 'processing',
        upload_progress_mode: 'indeterminate',
        upload_status_label: 'Reintentando procesamiento en el servidor…',
        error_message: null,
      });
    }

    await firstValueFrom(this.api.retryMediaRecordingUpload(server_id));

    await this.waitForMediaRecordingUntilTerminal(server_id, {
      recordable_type: recordable_context.recordable_type,
      recordable_id: recordable_context.recordable_id,
      media_type: active_state?.media_type ?? 'video',
      client_local_id: client_local_id ?? active_state?.client_local_id ?? null,
    });

    if (client_local_id !== null) {
      this.removeMetadata(client_local_id);
      this.refreshPendingUploads();
    }
  }

  async retryMediaRecordingUploadHybrid(
    client_local_id: string,
    server_recording: MediaRecording | null = null
  ): Promise<MediaRecordingUploadRetryContext> {
    const has_local_retry_data = await this.hasLocalRetryData(client_local_id);

    const recording = server_recording ?? (await this.resolveServerRecordingForRetry(client_local_id));

    if (recording === null) {
      const is_server_recording_missing_without_local_retry_data = !has_local_retry_data;

      if (is_server_recording_missing_without_local_retry_data) {
        throw new Error('No se encontró la grabación en el servidor para reintentar.');
      }

      const local_only_retry_context: MediaRecordingUploadRetryContext = {
        can_retry: true,
        kind: MediaRecordingUploadRetryKindEnum.ChunkUpload,
        failure_phase: MediaRecordingUploadFailurePhaseEnum.ChunkUpload,
        failure_summary: 'La grabación no existe en el servidor; se recreará desde la copia local.',
        retry_summary: 'Se creará la sesión en el servidor y se subirán los fragmentos desde este navegador.',
        button_label: 'Reintentar subida de fragmentos',
      };

      await this.retryUpload(client_local_id);

      return local_only_retry_context;
    }

    const retry_context = resolveMediaRecordingUploadRetryContext(recording, has_local_retry_data);

    if (retry_context === null) {
      throw new Error('Esta grabación no admite reintento de subida.');
    }

    if (!retry_context.can_retry || retry_context.kind === null) {
      throw new Error(retry_context.failure_summary);
    }

    if (retry_context.kind === MediaRecordingUploadRetryKindEnum.ServerProcessing) {
      await this.retryServerProcessing(recording.id, client_local_id);

      return retry_context;
    }

    await this.retryUpload(client_local_id);

    return retry_context;
  }

  async retryUpload(client_local_id: string): Promise<void> {
    const metadata = await this.resolveRetryMetadata(client_local_id);

    if (metadata === null) {
      throw new Error('No se encontró la grabación para reintentar la subida.');
    }

    this.patchActiveState({
      client_local_id: metadata.client_local_id,
      server_id: metadata.server_id ?? null,
      concept: metadata.concept,
      youtube_title: metadata.youtube_title ?? null,
      media_type: metadata.media_type,
      capture_mode: metadata.capture_mode,
      recordable_type: metadata.recordable_type ?? null,
      recordable_id: metadata.recordable_id ?? null,
      status: 'uploading',
      elapsed_seconds: metadata.duration_seconds ?? 0,
      upload_progress_percent: 0,
      upload_progress_mode: 'determinate',
      upload_status_label: 'Reintentando subida…',
      error_message: null,
    });

    this.upsertMetadata({ ...metadata, status: 'uploading' });
    this.refreshPendingUploads();

    const server_id = await this.ensureServerSession(
      {
        client_local_id: metadata.client_local_id,
        server_id: metadata.server_id ?? null,
        concept: metadata.concept,
        youtube_title: metadata.youtube_title ?? null,
        media_type: metadata.media_type,
        capture_mode: metadata.capture_mode,
        include_microphone: metadata.media_type === 'audio' || metadata.capture_mode === 'microphone_and_screen',
        include_screen: metadata.media_type === 'video',
        include_system_audio: false,
        status: 'uploading',
        elapsed_seconds: metadata.duration_seconds ?? 0,
        upload_progress_percent: 0,
        upload_progress_mode: 'determinate',
        upload_status_label: 'Reintentando subida…',
        upload_retry_button_label: null,
        upload_retry_summary: null,
        error_message: null,
        recordable_type: metadata.recordable_type ?? null,
        recordable_id: metadata.recordable_id ?? null,
      },
      metadata.server_id ?? null
    );

    if (!server_id) {
      throw new Error('No se pudo recuperar la sesión de grabación en el servidor.');
    }

    try {
      await this.uploadRemainingChunks(client_local_id, server_id);

      const media_recording = await firstValueFrom(this.api.getMediaRecording(server_id));
      const expected_chunk_count =
        metadata.expected_chunk_count ??
        media_recording.expected_chunk_count ??
        Math.max(metadata.pending_chunk_indexes.length, media_recording.received_chunk_count ?? 0);

      if (media_recording.recording_status === 'recording' || media_recording.recording_status === 'paused') {
        await firstValueFrom(
          this.api.stopMediaRecording(server_id, {
            duration_seconds: metadata.duration_seconds,
            expected_chunk_count,
          })
        );
      } else if (media_recording.recording_status === 'upload_failed' || media_recording.recording_status === 'failed') {
        await firstValueFrom(this.api.retryMediaRecordingUpload(server_id));
        await this.waitForMediaRecordingUntilTerminal(server_id, {
          recordable_type: metadata.recordable_type ?? null,
          recordable_id: metadata.recordable_id ?? null,
          media_type: metadata.media_type,
          client_local_id,
        });
        this.removeMetadata(client_local_id);
        this.refreshPendingUploads();
        this.scheduleAutoDismissForClientLocalId(client_local_id);
        this.notifyRecordingSessionEnded({
          recordable_type: metadata.recordable_type ?? null,
          recordable_id: metadata.recordable_id ?? null,
          server_id,
        });

        return;
      } else if (media_recording.recording_status === 'uploading') {
        if (areMediaRecordingChunksCompleteOnServer(media_recording)) {
          await this.finalizeMediaUpload(server_id, {
            client_local_id: metadata.client_local_id,
            server_id,
            concept: metadata.concept,
            youtube_title: metadata.youtube_title ?? null,
            media_type: metadata.media_type,
            capture_mode: metadata.capture_mode,
            include_microphone: metadata.media_type === 'audio' || metadata.capture_mode === 'microphone_and_screen',
            include_screen: metadata.media_type === 'video',
            include_system_audio: false,
            status: 'uploading',
            elapsed_seconds: metadata.duration_seconds ?? 0,
            upload_progress_percent: 100,
            upload_progress_mode: 'determinate',
            upload_status_label: 'Chunks subidos. Procesando en servidor…',
            upload_retry_button_label: null,
            upload_retry_summary: null,
            error_message: null,
            recordable_type: metadata.recordable_type ?? null,
            recordable_id: metadata.recordable_id ?? null,
          });
        } else {
          await this.waitForMediaRecordingUntilTerminal(server_id, {
            recordable_type: metadata.recordable_type ?? null,
            recordable_id: metadata.recordable_id ?? null,
            media_type: metadata.media_type,
            client_local_id,
          });
        }

        this.removeMetadata(client_local_id);
        this.refreshPendingUploads();
        this.scheduleAutoDismissForClientLocalId(client_local_id);
        this.notifyRecordingSessionEnded({
          recordable_type: metadata.recordable_type ?? null,
          recordable_id: metadata.recordable_id ?? null,
          server_id,
        });

        return;
      } else if (media_recording.recording_status === 'processing') {
        await this.waitForMediaRecordingUntilTerminal(server_id, {
          recordable_type: metadata.recordable_type ?? null,
          recordable_id: metadata.recordable_id ?? null,
          media_type: metadata.media_type,
          client_local_id,
        });
        this.removeMetadata(client_local_id);
        this.refreshPendingUploads();
        this.scheduleAutoDismissForClientLocalId(client_local_id);
        this.notifyRecordingSessionEnded({
          recordable_type: metadata.recordable_type ?? null,
          recordable_id: metadata.recordable_id ?? null,
          server_id,
        });

        return;
      } else if (media_recording.recording_status === 'completed') {
        this.patchActiveStateForClientLocalId(client_local_id, {
          status: 'completed',
          server_id,
          upload_progress_percent: 100,
          upload_progress_mode: 'determinate',
          upload_status_label: null,
          error_message: null,
        });
        this.removeMetadata(client_local_id);
        this.refreshPendingUploads();
        this.scheduleAutoDismissForClientLocalId(client_local_id);
        this.notifyRecordingSessionEnded({
          recordable_type: metadata.recordable_type ?? null,
          recordable_id: metadata.recordable_id ?? null,
          server_id,
        });

        return;
      }

      await this.finalizeMediaUpload(server_id, {
        client_local_id: metadata.client_local_id,
        server_id,
        concept: metadata.concept,
        youtube_title: metadata.youtube_title ?? null,
        media_type: metadata.media_type,
        capture_mode: metadata.capture_mode,
        include_microphone: metadata.media_type === 'audio' || metadata.capture_mode === 'microphone_and_screen',
        include_screen: metadata.media_type === 'video',
        include_system_audio: false,
        status: 'uploading',
        elapsed_seconds: metadata.duration_seconds ?? 0,
        upload_progress_percent: 100,
        upload_progress_mode: 'determinate',
        upload_status_label: 'Chunks subidos. Procesando en servidor…',
        upload_retry_button_label: null,
        upload_retry_summary: null,
        error_message: null,
        recordable_type: metadata.recordable_type ?? null,
        recordable_id: metadata.recordable_id ?? null,
      });

      this.removeMetadata(client_local_id);
      this.refreshPendingUploads();
      this.scheduleAutoDismissForClientLocalId(client_local_id);
      this.notifyRecordingSessionEnded({
        recordable_type: metadata.recordable_type ?? null,
        recordable_id: metadata.recordable_id ?? null,
        server_id,
      });
    } catch (error) {
      this.markUploadFailed(client_local_id, error);

      throw error;
    }
  }

  async drainOfflineQueue(): Promise<void> {
    if (!navigator.onLine) {
      return;
    }

    const queue = this.getMetadataQueue();

    for (const item of queue) {
      if (item.status === 'pending_upload' || item.status === 'upload_failed') {
        if (this.getLiveActiveRecordingState(item.client_local_id) !== null) {
          this.scheduleLivePendingChunkUpload(item.client_local_id, 0);
          continue;
        }

        await this.retryUpload(item.client_local_id);
      }
    }
  }

  isRecordingActive(): boolean {
    const status = this.active_state_subject.value?.status;

    return status === 'recording' || status === 'paused' || status === 'stopping';
  }

  private getLiveActiveRecordingState(client_local_id: string): ActiveRecordingState | null {
    const state = this.active_state_subject.value;

    if (state === null || state.client_local_id !== client_local_id) {
      return null;
    }

    return state.status === 'recording' || state.status === 'paused' || state.status === 'stopping' ? state : null;
  }

  isRecordingAvailableOnPlatform(): boolean {
    return this.platform.isDesktop();
  }

  async loadMicrophoneDevices(): Promise<RecordingMicrophoneLoadResult> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
      return {
        devices: [],
        error_message: 'No se pudo acceder al micrófono.',
      };
    }

    let electron_microphone_access_result: MicrophoneAccessRequestResult | null = null;
    if (this.platform.isElectronApp()) {
      electron_microphone_access_result = await this.platform.requestMicrophoneAccess();
    }

    try {
      const permission_stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      permission_stream.getTracks().forEach(track => track.stop());
    } catch (error) {
      return {
        devices: [],
        error_message: this.resolveMicrophonePermissionErrorMessage(error, electron_microphone_access_result),
      };
    }

    const devices = await navigator.mediaDevices.enumerateDevices();
    const audio_input_devices = devices.filter(device => device.kind === 'audioinput');

    return {
      devices: audio_input_devices.map((device, index) => ({
        device_id: device.deviceId,
        label: this.resolveMicrophoneDeviceLabel(device, index),
      })),
      error_message: null,
    };
  }

  async loadDisplaySourcesForRecordingSetup(): Promise<RecordingDisplaySourcesLoadResult> {
    if (!this.platform.isElectronApp()) {
      return {
        sources: [],
        supports_system_audio: false,
        error_message: null,
      };
    }

    const screen_access_result = await this.platform.requestScreenAccess();
    if (!screen_access_result.granted) {
      return {
        sources: [],
        supports_system_audio: false,
        error_message: this.resolveScreenPermissionErrorMessage(screen_access_result),
      };
    }

    const list_result = await this.platform.listDisplayMediaSources();

    return {
      sources: list_result.sources,
      supports_system_audio: list_result.supports_system_audio,
      error_message: null,
    };
  }

  canStartNewRecording(): boolean {
    return this.getNewRecordingBlockedMessage() === null;
  }

  getActiveRecordingBlockingNewOne(): ActiveRecordingState | null {
    if (!this.isRecordingActive()) {
      return null;
    }

    return this.active_state_subject.value;
  }

  getNewRecordingBlockedMessage(): string | null {
    if (!this.isRecordingAvailableOnPlatform()) {
      return 'La grabación solo está disponible en escritorio.';
    }

    const blocking_recording = this.getActiveRecordingBlockingNewOne();

    if (blocking_recording === null) {
      return null;
    }

    const status_label =
      blocking_recording.status === 'paused' ? 'pausada' : blocking_recording.status === 'stopping' ? 'deteniéndose' : 'en curso';

    return `Finaliza la grabación ${status_label} («${blocking_recording.concept}») desde la barra superior antes de iniciar una nueva.`;
  }

  private resolveMicrophonePermissionErrorMessage(
    error: unknown,
    electron_microphone_access_result: MicrophoneAccessRequestResult | null
  ): string {
    if (error instanceof DOMException) {
      if (error.name === 'NotAllowedError') {
        if (electron_microphone_access_result?.status_after === 'denied') {
          return 'Acceso al micrófono denegado. En macOS, si ya se negó antes, Electron no vuelve a mostrar el diálogo automáticamente: actívalo en Ajustes > Privacidad y seguridad > Micrófono y reinicia la app.';
        }

        if (electron_microphone_access_result?.status_after === 'restricted') {
          return 'El acceso al micrófono está restringido por el sistema. Revisa políticas de macOS o controles parentales.';
        }

        return 'No se pudo obtener permiso de micrófono. Acepta el diálogo del sistema o habilítalo en Ajustes > Privacidad y seguridad > Micrófono.';
      }

      if (error.name === 'NotFoundError') {
        return 'No se ha encontrado ningún micrófono disponible en este equipo.';
      }
    }

    return 'No se pudo acceder al micrófono.';
  }

  private resolveScreenPermissionErrorMessage(screen_access_result: ScreenAccessRequestResult): string {
    if (screen_access_result.status === 'denied') {
      return 'Grabación de pantalla denegada. En macOS debes habilitarla en Ajustes > Privacidad y seguridad > Grabación de pantalla y reiniciar la app.';
    }

    if (screen_access_result.status === 'restricted') {
      return 'La grabación de pantalla está restringida por el sistema. Revisa políticas de macOS o controles parentales.';
    }

    return 'No se pudo validar el permiso de grabación de pantalla.';
  }

  private resolveMicrophoneDeviceLabel(device: MediaDeviceInfo, index: number): string {
    const trimmed_label = device.label?.trim();

    if (trimmed_label) {
      return trimmed_label;
    }

    return `Micrófono ${index + 1}`;
  }

  private startCurrentRecordingSegment(): void {
    if (!this.media_stream || !this.recording_mime_type || !this.recording_media_type || !this.recording_client_local_id) {
      throw new Error('No se pudo iniciar el segmento de grabación.');
    }

    this.media_recorder = new MediaRecorder(this.media_stream, { mimeType: this.recording_mime_type });
    this.setupMediaRecorderHandlers(this.recording_client_local_id, this.recording_media_type);
    this.media_recorder.start();
    this.scheduleSegmentRotation();
  }

  private scheduleSegmentRotation(): void {
    this.clearSegmentRotationTimer();

    const state = this.active_state_subject.value;

    if (!state || state.status !== 'recording') {
      return;
    }

    this.segment_rotation_timer = setTimeout(() => {
      void this.rotateRecordingSegment();
    }, RECORDING_SEGMENT_DURATION_MS);
  }

  private clearSegmentRotationTimer(): void {
    if (this.segment_rotation_timer !== null) {
      clearTimeout(this.segment_rotation_timer);
      this.segment_rotation_timer = null;
    }
  }

  private async rotateRecordingSegment(): Promise<void> {
    if (this.is_discarding) {
      return;
    }

    const state = this.active_state_subject.value;

    if (!state || state.status !== 'recording') {
      return;
    }

    await this.finalizeCurrentSegment(false);
    this.recording_segment_index += 1;
    this.recording_part_index = 0;
    this.startCurrentRecordingSegment();
  }

  private async finalizeCurrentSegment(wait_for_uploads = true): Promise<void> {
    this.clearSegmentRotationTimer();

    if (!this.media_recorder || this.media_recorder.state === 'inactive') {
      return;
    }

    const recorder = this.media_recorder;
    this.media_recorder = null;

    await new Promise<void>(resolve => {
      recorder.addEventListener('stop', () => resolve(), { once: true });
      recorder.requestData();
      recorder.stop();
    });

    if (wait_for_uploads) {
      await this.flushPendingChunkUploads();
    }
  }

  private setupMediaRecorderHandlers(client_local_id: string, media_type: MediaRecordingMediaType): void {
    if (!this.media_recorder) {
      return;
    }

    this.media_recorder.addEventListener('dataavailable', event => {
      if (this.is_discarding || !event.data || event.data.size === 0) {
        return;
      }

      if (media_type === 'audio') {
        this.chunk_blobs.push(event.data);
      }

      const segment_index = this.recording_segment_index;
      const upload_slices = this.splitBlobForServerUpload(event.data);

      for (const slice of upload_slices) {
        const current_index = this.chunk_index;
        const part_index = this.recording_part_index;
        this.chunk_index += 1;
        this.recording_part_index += 1;
        this.syncPersistedActiveRecordingSession();

        this.enqueueLiveChunkUpload(() =>
          this.handleChunkAvailable(client_local_id, current_index, slice, {
            segment_index,
            part_index,
          })
        );
      }
    });
  }

  splitBlobForServerUpload(blob: Blob): Blob[] {
    if (blob.size <= SERVER_UPLOAD_SLICE_BYTES) {
      return [blob];
    }

    const slices: Blob[] = [];
    let offset = 0;

    while (offset < blob.size) {
      const end = Math.min(offset + SERVER_UPLOAD_SLICE_BYTES, blob.size);
      slices.push(blob.slice(offset, end));
      offset = end;
    }

    return slices;
  }

  private trackChunkUpload(upload_promise: Promise<void>): void {
    this.pending_chunk_uploads.add(upload_promise);

    void upload_promise.finally(() => {
      this.pending_chunk_uploads.delete(upload_promise);
    });
  }

  private enqueueLiveChunkUpload(upload_task: () => Promise<void>): void {
    const upload_promise = this.live_chunk_upload_chain.then(upload_task, upload_task);
    this.live_chunk_upload_chain = upload_promise.catch(() => undefined);
    this.trackChunkUpload(upload_promise);
  }

  private async flushPendingChunkUploads(): Promise<void> {
    await Promise.all([...this.pending_chunk_uploads]);
  }

  private async handleChunkAvailable(
    client_local_id: string,
    chunk_index: number,
    blob: Blob,
    segment_metadata: { segment_index: number; part_index: number }
  ): Promise<void> {
    if (this.is_discarding) {
      return;
    }

    const state = this.active_state_subject.value;
    const state_belongs_to_chunk = state !== null && state.client_local_id === client_local_id;

    if (!navigator.onLine) {
      await this.saveChunkForRetry(client_local_id, chunk_index, blob, segment_metadata);
      return;
    }

    try {
      let server_id: number | null = null;

      if (state_belongs_to_chunk) {
        server_id = state.server_id ?? (await this.ensureServerSession(state, state.server_id));
      } else {
        const metadata = this.getMetadataQueue().find(item => item.client_local_id === client_local_id);
        server_id = metadata?.server_id ?? null;
      }

      if (!server_id) {
        await this.saveChunkForRetry(client_local_id, chunk_index, blob, segment_metadata);
        return;
      }

      await firstValueFrom(this.api.uploadMediaRecordingChunk(server_id, chunk_index, blob, undefined, segment_metadata));
      await this.deleteChunk(client_local_id, chunk_index);
      this.uploaded_chunk_count += 1;
      this.markChunkUploadedInOfflineMetadata(client_local_id, chunk_index, server_id);
      this.updateUploadProgress(this.uploaded_chunk_count, this.chunk_index);
    } catch {
      await this.saveChunkForRetry(client_local_id, chunk_index, blob, segment_metadata);
    }
  }

  private async uploadRemainingChunks(client_local_id: string, server_id: number): Promise<void> {
    const chunks = await this.getChunksForRecording(client_local_id);
    const total = Math.max(chunks.length + this.uploaded_chunk_count, this.chunk_index);

    for (const chunk of chunks) {
      const segment_metadata = this.resolveOfflineChunkSegmentMetadata(chunk);

      await firstValueFrom(
        this.api.uploadMediaRecordingChunk(server_id, chunk.chunk_index, chunk.blob, chunk.duration_ms, segment_metadata)
      );
      await this.deleteChunk(client_local_id, chunk.chunk_index);
      this.uploaded_chunk_count += 1;
      this.markChunkUploadedInOfflineMetadata(client_local_id, chunk.chunk_index, server_id);
      this.updateUploadProgress(this.uploaded_chunk_count, total);
    }
  }

  private async finalizeMediaUpload(server_id: number, state: ActiveRecordingState): Promise<void> {
    await firstValueFrom(this.api.finalizeMediaRecordingUpload(server_id));

    const upload_status_label = state.media_type === 'audio' ? 'En cola en el servidor…' : 'Chunks subidos. Procesando en servidor…';

    this.patchActiveStateForClientLocalId(state.client_local_id, {
      status: 'processing',
      server_id,
      upload_progress_percent: 100,
      upload_progress_mode: 'indeterminate',
      upload_status_label,
      error_message: null,
    });

    // No esperamos el terminal (YouTube/transcripción): el acta muestra el progreso.
    // El chip superior solo confirma "en cola/procesando" y se puede ocultar con la X.
    this.scheduleAutoDismissForClientLocalId(state.client_local_id, 4000);
  }

  private async waitForMediaRecordingUntilTerminal(
    server_id: number,
    context?: Pick<ActiveRecordingState, 'recordable_type' | 'recordable_id' | 'media_type'> & {
      client_local_id?: string | null;
    }
  ): Promise<void> {
    const expected_client_local_id = context?.client_local_id ?? this.active_state_subject.value?.client_local_id ?? null;
    const media_recording = await firstValueFrom(this.api.getMediaRecording(server_id));

    let recordable_type =
      media_recording.recordable_type ?? context?.recordable_type ?? this.active_state_subject.value?.recordable_type ?? null;
    let recordable_id = media_recording.recordable_id ?? context?.recordable_id ?? this.active_state_subject.value?.recordable_id ?? null;
    const media_type = media_recording.media_type ?? context?.media_type ?? this.active_state_subject.value?.media_type ?? 'video';

    if (expected_client_local_id !== null && isMediaRecordingLinkedToMeetingRecord(recordable_type, recordable_id)) {
      this.patchActiveStateForClientLocalId(expected_client_local_id, {
        recordable_type,
        recordable_id,
      });
    }

    const meeting_record_id = this.resolveMeetingRecordId(recordable_type, recordable_id);

    if (meeting_record_id !== null) {
      await this.waitForMediaRecordingUntilTerminalViaMeetingRecordPusher(
        server_id,
        meeting_record_id,
        media_type,
        expected_client_local_id
      );

      return;
    }

    await this.pollMediaRecordingUntilTerminal(server_id, expected_client_local_id);
  }

  private resolveMeetingRecordId(recordable_type: string | null, recordable_id: number | null): number | null {
    if (!isMediaRecordingLinkedToMeetingRecord(recordable_type, recordable_id)) {
      return null;
    }

    return recordable_id;
  }

  private async waitForMediaRecordingUntilTerminalViaMeetingRecordPusher(
    server_id: number,
    meeting_record_id: number,
    media_type: MediaRecordingMediaType,
    expected_client_local_id: string | null
  ): Promise<void> {
    await new Promise<void>((resolve, reject) => {
      let is_settled = false;
      let fallback_poll_timer: ReturnType<typeof setInterval> | null = null;
      let max_wait_timer: ReturnType<typeof setTimeout> | null = null;

      const settle = (): void => {
        if (is_settled) {
          return;
        }

        is_settled = true;

        if (fallback_poll_timer !== null) {
          clearInterval(fallback_poll_timer);
        }

        if (max_wait_timer !== null) {
          clearTimeout(max_wait_timer);
        }

        unsubscribe_from_pusher();
      };

      const handle_terminal_media_recording = (media_recording: MediaRecording): void => {
        if (media_recording.recording_status === 'completed') {
          if (expected_client_local_id !== null) {
            this.patchActiveStateForClientLocalId(expected_client_local_id, {
              status: 'completed',
              server_id,
              upload_progress_percent: 100,
              upload_progress_mode: 'determinate',
              upload_status_label: null,
            });
            this.scheduleAutoDismissForClientLocalId(expected_client_local_id);
          }
          settle();
          resolve();

          return;
        }

        if (media_recording.recording_status === 'failed' || media_recording.recording_status === 'upload_failed') {
          this.patchActiveStateFromTerminalFailure(media_recording, server_id, expected_client_local_id);
          settle();
          resolve();

          return;
        }

        this.patchActiveStateFromMediaRecording(media_recording, server_id, expected_client_local_id);
      };

      const handle_pusher_update = (update: MeetingRecordMediaRecordingPusherUpdate): void => {
        if (update.media_recording_id !== server_id) {
          return;
        }

        handle_terminal_media_recording(this.buildMediaRecordingFromPusherUpdate(update, media_type));
      };

      const unsubscribe_from_pusher = this.pusher.subscribeToMeetingRecordChannel(meeting_record_id, handle_pusher_update);

      fallback_poll_timer = setInterval(() => {
        if (is_settled) {
          return;
        }

        void firstValueFrom(this.api.getMediaRecording(server_id))
          .then(media_recording => {
            if (is_settled) {
              return;
            }

            handle_terminal_media_recording(media_recording);
          })
          .catch(() => undefined);
      }, MEDIA_RECORDING_TERMINAL_POLL_INTERVAL_MS);

      max_wait_timer = setTimeout(() => {
        if (is_settled) {
          return;
        }

        settle();
        reject(new Error('Tiempo de espera agotado al procesar la grabación.'));
      }, MEDIA_RECORDING_TERMINAL_MAX_WAIT_MS);
    });
  }

  private buildMediaRecordingFromPusherUpdate(
    update: MeetingRecordMediaRecordingPusherUpdate,
    media_type: MediaRecordingMediaType
  ): MediaRecording {
    return {
      id: update.media_recording_id,
      recording_status: update.recording_status,
      server_processing_status: update.server_processing_status,
      youtube_upload_status: update.youtube_upload_status as MediaRecording['youtube_upload_status'],
      transcription_status: update.transcription_status,
      media_type,
    } as MediaRecording;
  }

  private patchActiveStateFromTerminalFailure(
    media_recording: MediaRecording,
    server_id: number,
    expected_client_local_id: string | null
  ): void {
    const active_state = this.active_state_subject.value;

    if (expected_client_local_id !== null && active_state?.client_local_id !== expected_client_local_id) {
      return;
    }

    const client_local_id = active_state?.client_local_id ?? media_recording.client_local_id ?? '';
    const has_local_retry_data = client_local_id !== '' && this.hasLocalRetryDataInQueue(client_local_id);
    const retry_context = resolveMediaRecordingUploadRetryContext(media_recording, has_local_retry_data);

    if (active_state !== null) {
      // Solo persiste metadata si hay chunks pendientes reales; si no, limpia basura.
      this.persistFailedUploadMetadataSync(active_state.client_local_id);
    }

    const failure_patch: Partial<ActiveRecordingState> = {
      status: media_recording.recording_status === 'upload_failed' ? 'upload_failed' : 'failed',
      server_id,
      upload_progress_mode: 'determinate',
      upload_status_label: null,
      error_message: retry_context?.failure_summary ?? 'Error al procesar la grabación.',
      upload_retry_button_label: retry_context?.can_retry ? retry_context.button_label : null,
      upload_retry_summary: retry_context?.retry_summary ?? null,
    };

    if (expected_client_local_id !== null) {
      this.patchActiveStateForClientLocalId(expected_client_local_id, failure_patch);

      return;
    }

    this.patchActiveState(failure_patch);
  }

  private patchActiveStateFromMediaRecording(
    media_recording: MediaRecording,
    server_id: number,
    expected_client_local_id: string | null
  ): void {
    const progress_patch: Partial<ActiveRecordingState> = {
      status: media_recording.recording_status === 'processing' ? 'processing' : 'uploading',
      server_id,
      upload_progress_mode: 'indeterminate',
      upload_status_label: this.media_recording_server_processing_label_pipe.transform(media_recording),
    };

    if (expected_client_local_id !== null) {
      this.patchActiveStateForClientLocalId(expected_client_local_id, progress_patch);

      return;
    }

    this.patchActiveState(progress_patch);
  }

  private async pollMediaRecordingUntilTerminal(server_id: number, expected_client_local_id: string | null): Promise<void> {
    const poll_interval_ms = MEDIA_RECORDING_TERMINAL_POLL_INTERVAL_MS;
    const max_attempts = Math.ceil(MEDIA_RECORDING_TERMINAL_MAX_WAIT_MS / poll_interval_ms);

    for (let attempt = 0; attempt < max_attempts; attempt += 1) {
      const media_recording = await firstValueFrom(this.api.getMediaRecording(server_id));

      if (media_recording.recording_status === 'completed') {
        if (expected_client_local_id !== null) {
          this.patchActiveStateForClientLocalId(expected_client_local_id, {
            status: 'completed',
            server_id,
            upload_progress_percent: 100,
            upload_progress_mode: 'determinate',
            upload_status_label: null,
          });
          this.scheduleAutoDismissForClientLocalId(expected_client_local_id);
        }

        return;
      }

      if (media_recording.recording_status === 'failed' || media_recording.recording_status === 'upload_failed') {
        this.patchActiveStateFromTerminalFailure(media_recording, server_id, expected_client_local_id);

        return;
      }

      this.patchActiveStateFromMediaRecording(media_recording, server_id, expected_client_local_id);

      await new Promise(resolve => setTimeout(resolve, poll_interval_ms));
    }

    throw new Error('Tiempo de espera agotado al procesar la grabación.');
  }

  private notifyRecordingSessionEnded(event: RecordingSessionEndedEvent): void {
    this.recording_session_ended_subject.next(event);
  }

  private scheduleAutoDismiss(delay_ms = 4000): void {
    const client_local_id = this.active_state_subject.value?.client_local_id ?? null;

    if (client_local_id === null) {
      this.clearAutoDismissTimer();

      return;
    }

    this.scheduleAutoDismissForClientLocalId(client_local_id, delay_ms);
  }

  private scheduleAutoDismissForClientLocalId(client_local_id: string, delay_ms = 4000): void {
    this.clearAutoDismissTimer();

    this.auto_dismiss_timer = setTimeout(() => {
      const current = this.active_state_subject.value;

      if (current?.client_local_id === client_local_id) {
        this.dismissActiveRecording();
      }
    }, delay_ms);
  }

  private clearAutoDismissTimer(): void {
    if (this.auto_dismiss_timer !== null) {
      clearTimeout(this.auto_dismiss_timer);
      this.auto_dismiss_timer = null;
    }
  }

  private async ensureServerSession(state: ActiveRecordingState, server_id: number | null): Promise<number | null> {
    if (server_id) {
      return server_id;
    }

    const created = await firstValueFrom(
      this.api.createMediaRecording({
        concept: state.concept,
        client_local_id: state.client_local_id,
        media_type: state.media_type,
        capture_mode: state.capture_mode,
        recordable_type: state.recordable_type,
        recordable_id: state.recordable_id,
      })
    );

    this.patchActiveStateForClientLocalId(state.client_local_id, { server_id: created.id });
    this.syncOfflineMetadataServerId(state.client_local_id, created.id);

    return created.id;
  }

  private async resolveServerRecordingForRetry(client_local_id: string): Promise<MediaRecording | null> {
    const queued_metadata = this.getMetadataQueue().find(item => item.client_local_id === client_local_id);
    const active_state = this.active_state_subject.value;
    const server_id =
      queued_metadata?.server_id ?? (active_state?.client_local_id === client_local_id ? (active_state.server_id ?? null) : null);

    if (server_id !== null) {
      return await firstValueFrom(this.api.getMediaRecording(server_id));
    }

    const recordable_id =
      queued_metadata?.recordable_id ?? (active_state?.client_local_id === client_local_id ? active_state.recordable_id : null);

    if (recordable_id !== null) {
      const meeting_recordings = await firstValueFrom(this.api.getMeetingRecordMediaRecordings(recordable_id));
      const matched = meeting_recordings.find(item => item.client_local_id === client_local_id);

      if (matched !== undefined) {
        return matched;
      }
    }

    const indexed = await firstValueFrom(this.api.getMediaRecordings({ client_local_id }));
    const indexed_recordings = Array.isArray(indexed) ? (indexed as MediaRecording[]) : (indexed.data ?? []);
    const indexed_match = indexed_recordings.find(item => item.client_local_id === client_local_id);

    if (indexed_match !== undefined) {
      return indexed_match;
    }

    return null;
  }

  shouldShowPendingUploadBanner(
    pending: Pick<RecordingPendingUploadMetadata, 'client_local_id' | 'pending_chunk_indexes' | 'status' | 'recordable_id'>,
    active: ActiveRecordingState | null,
    meeting_record_id: number | undefined
  ): boolean {
    const has_pending_chunks = pending.pending_chunk_indexes.length > 0;

    // Sin chunks locales no hay "subida pendiente" útil en la barra (el reintento de servidor vive en el acta).
    if (!has_pending_chunks) {
      return false;
    }

    if (active !== null && active.client_local_id === pending.client_local_id) {
      const is_active_session =
        active.status === 'recording' ||
        active.status === 'paused' ||
        active.status === 'stopping' ||
        active.status === 'uploading' ||
        active.status === 'pending_upload' ||
        active.status === 'upload_failed' ||
        active.status === 'processing';

      if (is_active_session) {
        return false;
      }
    }

    if (
      active !== null &&
      (active.status === 'recording' || active.status === 'paused' || active.status === 'stopping') &&
      meeting_record_id !== undefined &&
      (pending.recordable_id === meeting_record_id || pending.recordable_id == null)
    ) {
      return false;
    }

    return true;
  }

  private syncOfflineMetadataServerId(client_local_id: string, server_id: number): void {
    const existing = this.getMetadataQueue().find(item => item.client_local_id === client_local_id);

    if (existing === undefined) {
      return;
    }

    this.upsertMetadata({
      ...existing,
      server_id,
    });
    this.refreshPendingUploads();
  }

  private markChunkUploadedInOfflineMetadata(client_local_id: string, chunk_index: number, server_id: number): void {
    const existing = this.getMetadataQueue().find(item => item.client_local_id === client_local_id);

    if (existing === undefined) {
      return;
    }

    const pending_chunk_indexes = existing.pending_chunk_indexes.filter(index => index !== chunk_index);

    if (pending_chunk_indexes.length === 0) {
      this.removeMetadata(client_local_id);
      this.refreshPendingUploads();

      return;
    }

    this.upsertMetadata({
      ...existing,
      server_id,
      pending_chunk_indexes,
    });
    this.refreshPendingUploads();
  }

  private assertRecordingAllowedOnPlatform(): void {
    if (!this.isRecordingAvailableOnPlatform()) {
      throw new Error('La grabación solo está disponible en escritorio.');
    }
  }

  private resolveRecordingConfig(
    include_microphone: boolean,
    include_screen: boolean
  ): { media_type: MediaRecordingMediaType; capture_mode: MediaRecordingCaptureMode } {
    if (include_screen) {
      return {
        media_type: 'video',
        capture_mode: 'microphone_and_screen',
      };
    }

    return {
      media_type: 'audio',
      capture_mode: 'microphone',
    };
  }

  private async acquireMediaStream(
    include_microphone: boolean,
    include_screen: boolean,
    microphone_device_id: string | null = null
  ): Promise<MediaStream> {
    this.assertRecordingAllowedOnPlatform();

    this.display_audio_track_ids.clear();
    this.captured_system_audio = false;
    const audio_constraints = this.resolveMicrophoneAudioConstraints(microphone_device_id);

    if (include_screen) {
      const display_stream = this.platform.isElectronApp()
        ? await this.acquireElectronDisplayStream()
        : await navigator.mediaDevices.getDisplayMedia({
            video: true,
            audio: true,
          });
      const video_tracks = [...display_stream.getVideoTracks()];
      const audio_tracks: MediaStreamTrack[] = [];
      const display_audio_tracks = display_stream.getAudioTracks();

      this.captured_system_audio = display_audio_tracks.length > 0;

      for (const audio_track of display_audio_tracks) {
        this.display_audio_track_ids.add(audio_track.id);
        audio_tracks.push(audio_track);
      }

      if (include_microphone) {
        const mic_stream = await navigator.mediaDevices.getUserMedia({ audio: audio_constraints });
        audio_tracks.push(...mic_stream.getAudioTracks());
      }

      const mixed_audio_track = this.mixAudioTracks(audio_tracks);
      const tracks = [...video_tracks];

      if (mixed_audio_track !== null) {
        tracks.push(mixed_audio_track);
      }

      return new MediaStream(tracks);
    }

    return navigator.mediaDevices.getUserMedia({ audio: audio_constraints });
  }

  private async acquireElectronDisplayStream(): Promise<MediaStream> {
    const display_stream_promise = navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: true,
    });

    const picker_request = await firstValueFrom(this.platform.display_media_picker_request$.pipe(take(1)));
    const preselection = this.pending_electron_display_selection;
    this.pending_electron_display_selection = null;

    if (preselection !== null) {
      const selection_confirmed = await this.platform.selectDisplayMediaSource(preselection.source_id, preselection.include_system_audio);

      if (!selection_confirmed) {
        await this.platform.cancelDisplayMediaPicker();

        throw new DOMException('No se pudo iniciar la captura de pantalla.', 'NotAllowedError');
      }
    } else {
      const picker_result = await this.openElectronDisplayMediaPicker(picker_request);

      if (picker_result === undefined) {
        await this.platform.cancelDisplayMediaPicker();

        throw new DOMException('La selección de pantalla fue cancelada.', 'NotAllowedError');
      }

      const selection_confirmed = await this.platform.selectDisplayMediaSource(picker_result.source_id, picker_result.include_system_audio);

      if (!selection_confirmed) {
        throw new DOMException('No se pudo iniciar la captura de pantalla.', 'NotAllowedError');
      }
    }

    return display_stream_promise;
  }

  private openElectronDisplayMediaPicker(
    picker_request: ElectronDisplayMediaPickerRequest
  ): Promise<ElectronDisplayMediaPickerDialogResult | undefined> {
    return firstValueFrom(
      this.mat_dialog
        .open(ElectronDisplayMediaPickerDialogComponent, {
          width: '520px',
          disableClose: true,
          data: {
            sources: picker_request.sources,
            supports_system_audio: picker_request.supports_system_audio,
          },
        })
        .afterClosed(),
      { defaultValue: undefined }
    );
  }

  private resolveMicrophoneAudioConstraints(microphone_device_id: string | null): boolean | MediaTrackConstraints {
    if (microphone_device_id === null || microphone_device_id === '') {
      return true;
    }

    return { deviceId: { exact: microphone_device_id } };
  }

  /**
   * MediaRecorder solo codifica una pista de audio; si hay micrófono y audio de
   * pantalla hay que mezclarlos en una sola pista con Web Audio API.
   */
  private mixAudioTracks(audio_tracks: MediaStreamTrack[]): MediaStreamTrack | null {
    if (audio_tracks.length === 0) {
      return null;
    }

    if (audio_tracks.length === 1) {
      return audio_tracks[0];
    }

    this.audio_context = new AudioContext();
    void this.audio_context.resume();
    const destination = this.audio_context.createMediaStreamDestination();

    for (const audio_track of audio_tracks) {
      const source = this.audio_context.createMediaStreamSource(new MediaStream([audio_track]));
      source.connect(destination);
    }

    return destination.stream.getAudioTracks()[0] ?? null;
  }

  private hasSystemAudioTrack(stream: MediaStream): boolean {
    if (this.captured_system_audio) {
      return true;
    }

    return stream.getAudioTracks().some(audio_track => this.display_audio_track_ids.has(audio_track.id));
  }

  private resolveMimeType(media_type: MediaRecordingMediaType): string {
    const candidates =
      media_type === 'video'
        ? ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm']
        : ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];

    for (const candidate of candidates) {
      if (MediaRecorder.isTypeSupported(candidate)) {
        return candidate;
      }
    }

    return candidates[candidates.length - 1];
  }

  private startElapsedTimer(): void {
    this.stopElapsedTimer();
    this.elapsed_timer = setInterval(() => {
      const state = this.active_state_subject.value;

      if (state) {
        this.patchActiveState({ elapsed_seconds: state.elapsed_seconds + 1 });
      }
    }, 1000);
  }

  private stopElapsedTimer(): void {
    if (this.elapsed_timer) {
      clearInterval(this.elapsed_timer);
      this.elapsed_timer = null;
    }
  }

  private releaseMediaStream(): void {
    this.detachMediaStreamTrackEndedListeners();
    this.media_stream?.getTracks().forEach(track => track.stop());
    this.media_stream = null;
    this.display_audio_track_ids.clear();
    this.captured_system_audio = false;

    if (this.audio_context !== null) {
      void this.audio_context.close();
      this.audio_context = null;
    }
  }

  private attachMediaStreamTrackEndedListeners(): void {
    this.detachMediaStreamTrackEndedListeners();

    if (!this.media_stream) {
      return;
    }

    const abort_controller = new AbortController();
    this.track_ended_abort_controller = abort_controller;

    for (const track of this.media_stream.getTracks()) {
      track.addEventListener(
        'ended',
        () => {
          void this.handleMediaTrackEnded();
        },
        { signal: abort_controller.signal }
      );
    }
  }

  private detachMediaStreamTrackEndedListeners(): void {
    this.track_ended_abort_controller?.abort();
    this.track_ended_abort_controller = null;
  }

  private async handleMediaTrackEnded(): Promise<void> {
    const state = this.active_state_subject.value;

    if (!state || state.status !== 'recording') {
      return;
    }

    if (this.media_recorder?.state === 'recording') {
      await this.pauseRecording();

      return;
    }

    this.stopElapsedTimer();

    if (state.server_id && navigator.onLine) {
      try {
        await firstValueFrom(this.api.pauseMediaRecording(state.server_id));
      } catch {
        // Ignore API errors when auto-pausing after the system stops capture.
      }
    }

    this.patchActiveState({ status: 'paused' });
  }

  private setActiveState(state: ActiveRecordingState): void {
    this.active_state_subject.next(state);
    this.syncPersistedActiveRecordingSession();
  }

  private patchActiveState(partial: Partial<ActiveRecordingState>): void {
    const current = this.active_state_subject.value;

    if (current) {
      this.active_state_subject.next({ ...current, ...partial });
      this.syncPersistedActiveRecordingSession();
    }
  }

  private patchActiveStateForClientLocalId(client_local_id: string, partial: Partial<ActiveRecordingState>): boolean {
    const current = this.active_state_subject.value;

    if (current === null || current.client_local_id !== client_local_id) {
      return false;
    }

    this.active_state_subject.next({ ...current, ...partial });
    this.syncPersistedActiveRecordingSession();

    return true;
  }

  private loadRecoverableRecordingSession(): void {
    if (this.active_state_subject.value !== null) {
      this.recoverable_session_subject.next(null);

      return;
    }

    const recoverable_session = this.readPersistedActiveRecordingSession();
    this.recoverable_session_subject.next(recoverable_session);
  }

  private readPersistedActiveRecordingSession(): RecoverableRecordingSession | null {
    const raw = localStorage.getItem(ACTIVE_SESSION_KEY);

    if (!raw) {
      return null;
    }

    try {
      const parsed = JSON.parse(raw) as RecoverableRecordingSession;

      if (parsed.status !== 'recording' && parsed.status !== 'paused') {
        localStorage.removeItem(ACTIVE_SESSION_KEY);

        return null;
      }

      return parsed;
    } catch {
      localStorage.removeItem(ACTIVE_SESSION_KEY);

      return null;
    }
  }

  private syncPersistedActiveRecordingSession(): void {
    const state = this.active_state_subject.value;

    if (state === null || (state.status !== 'recording' && state.status !== 'paused')) {
      localStorage.removeItem(ACTIVE_SESSION_KEY);
      this.recoverable_session_subject.next(null);

      return;
    }

    const recoverable_session: RecoverableRecordingSession = {
      client_local_id: state.client_local_id,
      server_id: state.server_id,
      concept: state.concept,
      youtube_title: state.youtube_title,
      media_type: state.media_type,
      capture_mode: state.capture_mode,
      include_microphone: state.include_microphone,
      include_screen: state.include_screen,
      include_system_audio: state.include_system_audio,
      status: state.status,
      elapsed_seconds: state.elapsed_seconds,
      recordable_type: state.recordable_type,
      recordable_id: state.recordable_id,
      chunk_index: this.chunk_index,
      recording_segment_index: this.recording_segment_index,
      recording_part_index: this.recording_part_index,
      uploaded_chunk_count: this.uploaded_chunk_count,
      interrupted_at: new Date().toISOString(),
    };

    localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify(recoverable_session));
    this.recoverable_session_subject.next(null);
  }

  private clearRecoverableRecordingSession(): void {
    localStorage.removeItem(ACTIVE_SESSION_KEY);
    this.recoverable_session_subject.next(null);
  }

  private updateUploadProgress(uploaded_count: number, total?: number): void {
    const state = this.active_state_subject.value;
    const is_live_recording = state?.status === 'recording' || state?.status === 'paused' || state?.status === 'stopping';

    if (is_live_recording) {
      return;
    }

    const denominator = total ?? Math.max(this.chunk_index, uploaded_count, 1);
    const percent = Math.min(100, Math.round((uploaded_count / denominator) * 100));
    const patch: Partial<ActiveRecordingState> = {
      upload_progress_percent: percent,
      upload_progress_mode: 'determinate',
      upload_status_label: percent >= 100 ? 'Chunks subidos. Procesando en servidor…' : `Subiendo chunks (${percent}%)…`,
      status: 'uploading',
    };

    this.patchActiveState(patch);
  }

  private persistOfflineMetadata(metadata: OfflineRecordingMetadata): void {
    this.upsertMetadata(metadata);
    this.refreshPendingUploads();
  }

  private enqueuePendingChunk(client_local_id: string, chunk_index: number): void {
    const queue = this.getMetadataQueue();
    const existing = queue.find(item => item.client_local_id === client_local_id);
    const pending = new Set(existing?.pending_chunk_indexes ?? []);
    pending.add(chunk_index);

    this.persistOfflineMetadata({
      client_local_id,
      concept: existing?.concept ?? this.active_state_subject.value?.concept ?? 'Grabación',
      media_type: existing?.media_type ?? this.active_state_subject.value?.media_type ?? 'audio',
      capture_mode: existing?.capture_mode ?? this.active_state_subject.value?.capture_mode ?? 'microphone',
      recordable_type: existing?.recordable_type ?? this.active_state_subject.value?.recordable_type ?? null,
      recordable_id: existing?.recordable_id ?? this.active_state_subject.value?.recordable_id ?? null,
      server_id: existing?.server_id ?? this.active_state_subject.value?.server_id ?? null,
      pending_chunk_indexes: Array.from(pending).sort((a, b) => a - b),
      status: 'pending_upload',
      duration_seconds: this.active_state_subject.value?.elapsed_seconds,
    });
    this.scheduleLivePendingChunkUpload(client_local_id);
  }

  private async saveChunkForRetry(
    client_local_id: string,
    chunk_index: number,
    blob: Blob,
    segment_metadata: { segment_index: number; part_index: number }
  ): Promise<void> {
    await this.saveChunk({
      client_local_id,
      chunk_index,
      segment_index: segment_metadata.segment_index,
      part_index: segment_metadata.part_index,
      blob,
    });
    this.enqueuePendingChunk(client_local_id, chunk_index);
  }

  private scheduleLivePendingChunkUpload(client_local_id: string, delay_ms = LIVE_PENDING_CHUNK_RETRY_DELAY_MS): void {
    if (
      this.live_pending_chunk_retry_timer !== null ||
      this.is_discarding ||
      this.is_stopping ||
      this.getLiveActiveRecordingState(client_local_id) === null
    ) {
      return;
    }

    this.live_pending_chunk_retry_timer = setTimeout(() => {
      this.live_pending_chunk_retry_timer = null;
      this.enqueueLiveChunkUpload(() => this.uploadPendingChunksForLiveRecording(client_local_id));
    }, delay_ms);
  }

  private clearLivePendingChunkRetryTimer(): void {
    if (this.live_pending_chunk_retry_timer !== null) {
      clearTimeout(this.live_pending_chunk_retry_timer);
      this.live_pending_chunk_retry_timer = null;
    }
  }

  private async uploadPendingChunksForLiveRecording(client_local_id: string): Promise<void> {
    if (!navigator.onLine || this.is_discarding || this.is_stopping) {
      return;
    }

    const state = this.getLiveActiveRecordingState(client_local_id);

    if (state === null) {
      return;
    }

    try {
      const server_id = await this.ensureServerSession(state, state.server_id);

      if (!server_id) {
        this.scheduleLivePendingChunkUpload(client_local_id);
        return;
      }

      await this.uploadRemainingChunks(client_local_id, server_id);
    } catch {
      this.scheduleLivePendingChunkUpload(client_local_id);
    }
  }

  private markUploadFailed(client_local_id: string, error: unknown): void {
    void this.getChunksForRecording(client_local_id).then(pending_chunks => {
      if (pending_chunks.length === 0) {
        // Sin chunks locales no tiene sentido dejar basura de "subida pendiente" en localStorage.
        this.removeMetadata(client_local_id);
        this.refreshPendingUploads();

        return;
      }

      this.persistFailedUploadMetadataSync(
        client_local_id,
        pending_chunks.map(chunk => chunk.chunk_index)
      );
      this.refreshPendingUploads();
    });

    this.patchActiveState({ status: 'upload_failed', error_message: this.extractErrorMessage(error) });
  }

  private persistFailedUploadMetadataSync(client_local_id: string, pending_chunk_indexes?: number[]): void {
    const existing = this.getMetadataQueue().find(item => item.client_local_id === client_local_id);
    const state = this.active_state_subject.value;
    const resolved_pending_chunk_indexes = pending_chunk_indexes ?? existing?.pending_chunk_indexes ?? [];
    const server_id = existing?.server_id ?? state?.server_id ?? null;
    const has_no_retry_anchor = resolved_pending_chunk_indexes.length === 0 && server_id === null;

    if (has_no_retry_anchor) {
      this.removeMetadata(client_local_id);

      return;
    }

    const metadata: OfflineRecordingMetadata = {
      client_local_id,
      concept: existing?.concept ?? state?.concept ?? 'Grabación',
      youtube_title: existing?.youtube_title ?? state?.youtube_title ?? null,
      media_type: existing?.media_type ?? state?.media_type ?? 'audio',
      capture_mode: existing?.capture_mode ?? state?.capture_mode ?? 'microphone',
      recordable_type: existing?.recordable_type ?? state?.recordable_type ?? null,
      recordable_id: existing?.recordable_id ?? state?.recordable_id ?? null,
      server_id,
      pending_chunk_indexes: resolved_pending_chunk_indexes,
      expected_chunk_count: existing?.expected_chunk_count ?? this.chunk_index,
      status: 'upload_failed',
      duration_seconds: existing?.duration_seconds ?? state?.elapsed_seconds,
    };

    this.upsertMetadata(metadata);
  }

  private async resolveRetryMetadata(client_local_id: string): Promise<OfflineRecordingMetadata | null> {
    const queued_metadata = this.getMetadataQueue().find(item => item.client_local_id === client_local_id);

    if (queued_metadata !== undefined) {
      return queued_metadata;
    }

    const active_state = this.active_state_subject.value;

    if (
      active_state === null ||
      active_state.client_local_id !== client_local_id ||
      (active_state.status !== 'upload_failed' && active_state.status !== 'pending_upload' && active_state.status !== 'failed')
    ) {
      return null;
    }

    const pending_chunks = await this.getChunksForRecording(client_local_id);

    return {
      client_local_id: active_state.client_local_id,
      concept: active_state.concept,
      youtube_title: active_state.youtube_title,
      media_type: active_state.media_type,
      capture_mode: active_state.capture_mode,
      recordable_type: active_state.recordable_type,
      recordable_id: active_state.recordable_id,
      server_id: active_state.server_id,
      pending_chunk_indexes: pending_chunks.map(chunk => chunk.chunk_index),
      expected_chunk_count: this.chunk_index,
      status: active_state.status === 'pending_upload' ? 'pending_upload' : 'upload_failed',
      duration_seconds: active_state.elapsed_seconds,
    };
  }

  private extractErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const error_body = error.error;

      if (typeof error_body === 'string' && error_body.trim() !== '') {
        return error_body;
      }

      if (typeof error_body === 'object' && error_body !== null) {
        const business_error = (error_body as { error?: string }).error;

        if (typeof business_error === 'string' && business_error.trim() !== '') {
          return business_error;
        }

        const message = (error_body as { message?: string }).message;

        if (typeof message === 'string' && message.trim() !== '') {
          return message;
        }
      }

      if (error.message.trim() !== '') {
        return error.message;
      }

      return `Error HTTP ${error.status} al subir la grabación.`;
    }

    if (error instanceof Error && error.message.trim() !== '') {
      return error.message;
    }

    return 'Error desconocido en la subida.';
  }

  private async saveChunk(record: OfflineChunkRecord): Promise<void> {
    const db = await this.openDb();
    await this.putInStore(db, CHUNKS_STORE, record);
  }

  private resolveOfflineChunkSegmentMetadata(chunk: OfflineChunkRecord): { segment_index: number; part_index: number } {
    const has_segment_index = typeof chunk.segment_index === 'number' && Number.isFinite(chunk.segment_index);
    const has_part_index = typeof chunk.part_index === 'number' && Number.isFinite(chunk.part_index);

    if (has_segment_index && has_part_index) {
      return {
        segment_index: chunk.segment_index,
        part_index: chunk.part_index,
      };
    }

    throw new Error(
      `El chunk ${chunk.chunk_index} guardado localmente no tiene metadatos de segmento. ` +
        'No se puede reintentar la subida de forma segura; contacta con soporte.'
    );
  }

  private async getChunksForRecording(client_local_id: string): Promise<OfflineChunkRecord[]> {
    const db = await this.openDb();
    const all = await this.getAllFromStore<OfflineChunkRecord>(db, CHUNKS_STORE);

    return all.filter(item => item.client_local_id === client_local_id).sort((a, b) => a.chunk_index - b.chunk_index);
  }

  private async deleteChunk(client_local_id: string, chunk_index: number): Promise<void> {
    const db = await this.openDb();
    await this.deleteFromStore(db, CHUNKS_STORE, `${client_local_id}:${chunk_index}`);
  }

  private async deleteAllChunksForRecording(client_local_id: string): Promise<void> {
    const chunks = await this.getChunksForRecording(client_local_id);

    for (const chunk of chunks) {
      await this.deleteChunk(client_local_id, chunk.chunk_index);
    }
  }

  private async purgeLocalRecordingArtifacts(client_local_id: string): Promise<void> {
    this.clearLivePendingChunkRetryTimer();
    await this.deleteAllChunksForRecording(client_local_id);
    await this.deleteVideoBlob(client_local_id);
    this.removeMetadata(client_local_id);
  }

  private async purgeStoredRecordingUpload(client_local_id: string, server_id: number | null): Promise<void> {
    await this.purgeLocalRecordingArtifacts(client_local_id);

    if (server_id !== null && navigator.onLine) {
      try {
        await firstValueFrom(this.api.deleteMediaRecording(server_id));
      } catch {
        // La cola local ya está limpia; el remoto puede quedar para limpieza manual.
      }
    }

    this.refreshPendingUploads();
  }

  private async saveVideoBlob(record: OfflineVideoRecord): Promise<void> {
    const db = await this.openDb();
    await this.putInStore(db, VIDEO_STORE, record);
  }

  private async getVideoBlob(client_local_id: string): Promise<Blob | null> {
    const db = await this.openDb();
    const record = await this.getFromStore<OfflineVideoRecord>(db, VIDEO_STORE, client_local_id);

    return record?.blob ?? null;
  }

  private async deleteVideoBlob(client_local_id: string): Promise<void> {
    const db = await this.openDb();
    await this.deleteFromStore(db, VIDEO_STORE, client_local_id);
  }

  private getMetadataQueue(): OfflineRecordingMetadata[] {
    const raw = localStorage.getItem(METADATA_KEY);

    if (!raw) {
      return [];
    }

    try {
      return JSON.parse(raw) as OfflineRecordingMetadata[];
    } catch {
      return [];
    }
  }

  /** Elimina entradas de cola sin chunks locales (p. ej. upload_failed huérfano tras un finalize ok). */
  private pruneEmptyPendingUploadMetadata(): void {
    const queue = this.getMetadataQueue();
    const pruned_queue = queue.filter(item => item.pending_chunk_indexes.length > 0);

    if (pruned_queue.length === queue.length) {
      return;
    }

    localStorage.setItem(METADATA_KEY, JSON.stringify(pruned_queue));
  }

  private upsertMetadata(metadata: OfflineRecordingMetadata): void {
    const queue = this.getMetadataQueue().filter(item => item.client_local_id !== metadata.client_local_id);
    queue.push({ ...metadata, updated_at: new Date().toISOString() });
    localStorage.setItem(METADATA_KEY, JSON.stringify(queue));
  }

  private removeMetadata(client_local_id: string): void {
    const queue = this.getMetadataQueue().filter(item => item.client_local_id !== client_local_id);
    localStorage.setItem(METADATA_KEY, JSON.stringify(queue));
  }

  private openDb(): Promise<IDBDatabase> {
    if (this.db_promise) {
      return this.db_promise;
    }

    this.db_promise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = () => {
        const db = request.result;

        if (!db.objectStoreNames.contains(CHUNKS_STORE)) {
          const store = db.createObjectStore(CHUNKS_STORE, { keyPath: 'id' });
          store.createIndex('client_local_id', 'client_local_id', { unique: false });
        }

        if (!db.objectStoreNames.contains(VIDEO_STORE)) {
          db.createObjectStore(VIDEO_STORE, { keyPath: 'client_local_id' });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    return this.db_promise;
  }

  private putInStore<T extends object>(db: IDBDatabase, store_name: string, value: T & { id?: string }): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(store_name, 'readwrite');
      const store = tx.objectStore(store_name);
      const payload = { ...value };

      if (store_name === CHUNKS_STORE && 'client_local_id' in payload && 'chunk_index' in payload) {
        payload.id = `${payload.client_local_id}:${payload.chunk_index}`;
      }

      store.put(payload);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private getFromStore<T>(db: IDBDatabase, store_name: string, key: IDBValidKey): Promise<T | undefined> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(store_name, 'readonly');
      const request = tx.objectStore(store_name).get(key);
      request.onsuccess = () => resolve(request.result as T | undefined);
      request.onerror = () => reject(request.error);
    });
  }

  private getAllFromStore<T>(db: IDBDatabase, store_name: string): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(store_name, 'readonly');
      const request = tx.objectStore(store_name).getAll();
      request.onsuccess = () => resolve(request.result as T[]);
      request.onerror = () => reject(request.error);
    });
  }

  private deleteFromStore(db: IDBDatabase, store_name: string, key: IDBValidKey): Promise<void> {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(store_name, 'readwrite');
      tx.objectStore(store_name).delete(key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  private async resolveRecordableContextForServerRetry(
    server_id: number,
    active_state: ActiveRecordingState | null
  ): Promise<{ recordable_type: string | null; recordable_id: number | null }> {
    if (active_state !== null) {
      return {
        recordable_type: active_state.recordable_type,
        recordable_id: active_state.recordable_id,
      };
    }

    const media_recording = await firstValueFrom(this.api.getMediaRecording(server_id));

    return {
      recordable_type: media_recording.recordable_type ?? null,
      recordable_id: media_recording.recordable_id ?? null,
    };
  }
}
